import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Code,
  Settings,
  TrendingUp,
  Layers,
  Database,
  Cloud,
  Smartphone,
  Brain,
  Wrench
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import technologyStackMasterService from '../../services/technologyStackMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const TechnologyStackMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTechnologyStacks: 0,
    activeTechnologyStacks: 0,
    inactiveTechnologyStacks: 0,
    frontendTechnologies: 0,
    backendTechnologies: 0,
    databaseTechnologies: 0,
    devopsTechnologies: 0,
    mobileTechnologies: 0,
    cloudTechnologies: 0,
    aiMlTechnologies: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedTechnologyStackData, setSelectedTechnologyStackData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [technologyStacksResponse, statsResponse] = await Promise.all([
        technologyStackMasterService.getAllTechnologyStackMasters(),
        technologyStackMasterService.getTechnologyStackMasterStats()
      ])
      
      if (technologyStacksResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (technologyStacksResponse.data || []).map(technologyStack => ({
          ...technologyStack,
          id: technologyStack._id,
          formattedEstimatedCost: technologyStack.estimatedCost ? 
            `${technologyStack.costCurrency} ${technologyStack.estimatedCost.toLocaleString()}` : 
            'Free',
          formattedPerformanceRating: `${technologyStack.performanceRating}/10`,
          formattedScalabilityRating: `${technologyStack.scalabilityRating}/10`,
          formattedMarketShare: `${technologyStack.marketShare}%`,
          formattedCompatibleTechnologies: technologyStack.compatibleTechnologies?.join(', ') || 'None',
          formattedSuitableProjectTypes: technologyStack.suitableProjectTypes?.join(', ') || 'Not Specified',
          currentVersion: `Rev ${technologyStack.revisionHistory?.length + 1 || 1}`
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (technologyStacksResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No Technology Stack Master records found. Create your first Technology Stack!')
        }
      } else {
        toast.error(technologyStacksResponse.message || 'Failed to load Technology Stack Master records')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading Technology Stack Master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view Technology Stack Master records')
      } else {
        toast.error('Failed to load Technology Stack Master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      key: 'technologyStackCode',
      label: 'Technology Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <Code size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'technologyStackName',
      label: 'Technology Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.technologyStackDescription && (
            <div className="text-sm text-gray-500">{row.technologyStackDescription}</div>
          )}
        </div>
      )
    },
    {
      key: 'technologyCategory',
      label: 'Category',
      sortable: true,
      render: (value) => {
        const categoryColors = {
          'Frontend': 'bg-blue-100 text-blue-800',
          'Backend': 'bg-green-100 text-green-800',
          'Database': 'bg-yellow-100 text-yellow-800',
          'DevOps': 'bg-purple-100 text-purple-800',
          'Mobile': 'bg-pink-100 text-pink-800',
          'Cloud': 'bg-indigo-100 text-indigo-800',
          'AI/ML': 'bg-orange-100 text-orange-800',
          'Other': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'technologyType',
      label: 'Type',
      sortable: true,
      render: (value) => {
        const typeColors = {
          'Framework': 'bg-blue-100 text-blue-800',
          'Library': 'bg-green-100 text-green-800',
          'Language': 'bg-yellow-100 text-yellow-800',
          'Database': 'bg-red-100 text-red-800',
          'Tool': 'bg-purple-100 text-purple-800',
          'Platform': 'bg-indigo-100 text-indigo-800',
          'Service': 'bg-pink-100 text-pink-800',
          'Other': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'formattedPerformanceRating',
      label: 'Performance',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-600">
          {value}
        </div>
      )
    },
    {
      key: 'technologyStackStatus',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'Active' ? 'bg-green-100 text-green-800' : 
          value === 'Deprecated' ? 'bg-red-100 text-red-800' :
          value === 'Legacy' ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {value}
        </span>
      )
    }
  ]

  const handleAddNew = () => {
    navigate('/projects/technology-stack-master/new')
  }

  const handleEdit = (row) => {
    navigate('/projects/technology-stack-master/edit', { 
      state: { technologyStackData: row } 
    })
  }

  const handleView = (row) => {
    navigate(`/projects/technology-stack-master/view/${row.id}`)
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await technologyStackMasterService.deleteTechnologyStackMaster(deleteItem.id)
      if (response.success) {
        toast.success('Technology Stack Master deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete Technology Stack Master')
      }
    } catch (error) {
      console.error('Error deleting Technology Stack Master:', error)
      toast.error('Failed to delete Technology Stack Master')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (row) => {
    setSelectedTechnologyStackData(row)
    setShowRevisionHistory(true)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Masters',
      path: '/projects?tab=masters',
      onClick: () => navigate('/projects?tab=masters')
    },
    {
      label: 'Technology Stack Master',
      path: '/projects/technology-stack-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Technology Stack Master</h1>
          <p>Manage technology stacks and frameworks for your organization</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Code size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalTechnologyStacks}</div>
          <div className="metric-title">Total Technologies</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Settings size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeTechnologyStacks}</div>
          <div className="metric-title">Active Technologies</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Layers size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.frontendTechnologies}</div>
          <div className="metric-title">Frontend Technologies</div>
          <div className="metric-subtitle">UI/UX frameworks</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Database size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.backendTechnologies}</div>
          <div className="metric-title">Backend Technologies</div>
          <div className="metric-subtitle">Server-side frameworks</div>
        </div>
      </div>

      {/* Technology Stack Master Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search technology stacks..."
          emptyMessage="No technology stacks found. Create your first technology stack!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Technology Stack Master"
        message={`Are you sure you want to delete "${deleteItem?.technologyStackName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedTechnologyStackData}
        title="Technology Stack Master Revision History"
      />
    </div>
  )
}

export default TechnologyStackMaster
